import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import { siteInfo } from '../data/mock';
import { EnquiryForm } from '../components/EnquiryForm';

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function ContactPage() {
  return (
    <main className="pt-20">
      {/* Header */}
      <section className="py-16 bg-[rgb(17,17,19)] relative">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="max-w-[1200px] mx-auto px-6 relative">
          <FadeInSection>
            <span className="text-[#F37021] text-sm font-semibold tracking-widest uppercase">Contact</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">Get In Touch</h1>
            <p className="text-[rgb(161,161,170)] text-lg max-w-2xl">
              Have questions? We're here to help. Reach out and our team will get back to you within 24 hours.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="py-20 bg-[rgb(17,17,19)]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-10">
            {/* Contact Info - Left Side */}
            <div className="lg:col-span-2 space-y-6">
              <FadeInSection>
                <h2 className="text-2xl font-bold text-white mb-8">Contact Information</h2>
                <div className="space-y-5">
                  {/* Phone */}
                  <a
                    href={`tel:${siteInfo.phone}`}
                    className="flex items-start gap-4 p-5 bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl hover:border-[rgba(243,112,33,0.35)] transition-all duration-200 group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[rgba(243,112,33,0.1)] flex items-center justify-center flex-shrink-0 group-hover:bg-[rgba(243,112,33,0.15)] transition-colors duration-200">
                      <Phone size={20} className="text-[#F37021]" />
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">Call Us</div>
                      <div className="text-[#F37021] text-base font-semibold">{siteInfo.phone}</div>
                      <div className="text-[rgb(161,161,170)] text-xs mt-1">Click to call directly</div>
                    </div>
                  </a>

                  {/* WhatsApp */}
                  <a
                    href={`https://wa.me/${siteInfo.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 p-5 bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl hover:border-[rgba(37,211,102,0.3)] transition-all duration-200 group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[rgba(37,211,102,0.1)] flex items-center justify-center flex-shrink-0 group-hover:bg-[rgba(37,211,102,0.15)] transition-colors duration-200">
                      <MessageCircle size={20} className="text-[#25D366]" />
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">WhatsApp</div>
                      <div className="text-[#25D366] text-base font-semibold">Chat with us</div>
                      <div className="text-[rgb(161,161,170)] text-xs mt-1">Quick response guaranteed</div>
                    </div>
                  </a>

                  {/* Email */}
                  <a
                    href={`mailto:${siteInfo.email}`}
                    className="flex items-start gap-4 p-5 bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl hover:border-[rgba(243,112,33,0.35)] transition-all duration-200 group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[rgba(243,112,33,0.1)] flex items-center justify-center flex-shrink-0 group-hover:bg-[rgba(243,112,33,0.15)] transition-colors duration-200">
                      <Mail size={20} className="text-[#F37021]" />
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">Email</div>
                      <div className="text-[rgb(218,218,218)] text-base">{siteInfo.email}</div>
                    </div>
                  </a>

                  {/* Address */}
                  <div className="flex items-start gap-4 p-5 bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl">
                    <div className="w-12 h-12 rounded-xl bg-[rgba(243,112,33,0.1)] flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} className="text-[#F37021]" />
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">Visit Us</div>
                      <div className="text-[rgb(218,218,218)] text-base">{siteInfo.address}</div>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start gap-4 p-5 bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl">
                    <div className="w-12 h-12 rounded-xl bg-[rgba(243,112,33,0.1)] flex items-center justify-center flex-shrink-0">
                      <Clock size={20} className="text-[#F37021]" />
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">Working Hours</div>
                      <div className="text-[rgb(218,218,218)] text-sm">Mon - Sat: 8:00 AM - 8:00 PM</div>
                      <div className="text-[rgb(161,161,170)] text-sm">Sunday: 10:00 AM - 4:00 PM</div>
                    </div>
                  </div>
                </div>
              </FadeInSection>
            </div>

            {/* Enquiry Form - Right Side */}
            <div className="lg:col-span-3">
              <FadeInSection delay={0.1}>
                <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 md:p-10 relative">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-[#F37021] rounded-t-2xl" />
                  <h3 className="text-2xl font-bold text-white mb-2">Send Us an Enquiry</h3>
                  <p className="text-[rgb(161,161,170)] text-sm mb-8">Fill the form below and we'll get back to you within 24 hours.</p>
                  <EnquiryForm />
                </div>
              </FadeInSection>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="py-12 bg-[rgb(17,17,19)]">
        <div className="max-w-[1200px] mx-auto px-6">
          <FadeInSection>
            <div className="rounded-2xl overflow-hidden border border-[rgba(255,255,255,0.1)]">
              <iframe
                title="Scaleup Academy Location"
                src={siteInfo.mapEmbedUrl}
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full grayscale contrast-125 opacity-80"
              />
            </div>
          </FadeInSection>
        </div>
      </section>
    </main>
  );
}
